import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, finalize } from 'rxjs';
import { ISchool, SchoolTypes } from '../../../interfaces/school.interface';
import { SchoolService } from '../../../services/school/school.service';
import { CoreConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-online-home',
  templateUrl: './online-home.component.html',
  styleUrls: ['./online-home.component.scss']
})
export class OnlineHomeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Data
  schools: ISchool[] = [];
  filteredSchools: ISchool[] = [];
  isLoading = true;
  searchQuery = '';

  // UI State
  selectedSchoolType = 'ALL';
  selectedLanguage = 'en';
  schoolTypes = [
    { label: 'ONLINE_ACCESS.SCHOOL_TYPES.ALL', value: 'ALL' },
    { label: 'ONLINE_ACCESS.SCHOOL_TYPES.PRIMARY', value: 'PRIMARY' },
    { label: 'ONLINE_ACCESS.SCHOOL_TYPES.SECONDARY', value: 'SECONDARY' },
    { label: 'ONLINE_ACCESS.SCHOOL_TYPES.HIGHER', value: 'HIGHER' },
    { label: 'ONLINE_ACCESS.SCHOOL_TYPES.UNIVERSITY', value: 'UNIVERSITY' }
  ];
  
  languages = [
    { label: 'LANGUAGES.ENGLISH', value: 'en', icon: 'pi pi-flag' },
    { label: 'LANGUAGES.FRENCH', value: 'fr', icon: 'pi pi-flag' }
  ];

  constructor(
    private schoolService: SchoolService,
    private router: Router,
    private translateService: TranslateService,
    private coreConfigService: CoreConfigService
  ) {
    // Configure the layout
    this.coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }



  ngOnInit(): void {
    // Initialize language from localStorage or default to 'en'
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    this.selectedLanguage = savedLanguage;
    this.translateService.use(savedLanguage);
    
    this.loadSchools();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads all schools from the API
   */
  private loadSchools(): void {
    this.isLoading = true;

    this.schoolService.allSchools({})
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            this.schools = response.data;
            this.filteredSchools = [...this.schools];
          }
        },
        error: (error) => {
          console.error('Error loading schools:', error);
          // In case of error, show some demo schools for UI purposes
          this.schools = this.getDemoSchools();
          this.filteredSchools = [...this.schools];
        }
      });
  }

  /**
   * Filters schools based on search query and school type
   */
  filterSchools(): void {
    this.filteredSchools = this.schools.filter(school => {
      const matchesSearch = school.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        school.address.toLowerCase().includes(this.searchQuery.toLowerCase());
      const matchesType = this.selectedSchoolType === 'ALL' || school.type === this.selectedSchoolType;

      return matchesSearch && matchesType;
    });
  }

  /**
   * Handles search input changes
   */
  onSearchChange(): void {
    this.filterSchools();
  }

  /**
   * Handles school type filter changes
   */
  onTypeFilterChange(): void {
    this.filterSchools();
  }

  /**
   * Handles language change
   */
  onLanguageChange(): void {
    this.translateService.use(this.selectedLanguage);
    localStorage.setItem('selectedLanguage', this.selectedLanguage);
  }

  /**
   * Navigates to school registration page
   */
  navigateToRegistration(school: ISchool): void {
    this.router.navigate(['/online-access/register', school.slug]);
  }

  /**
   * Gets school type display label
   */
  getSchoolTypeLabel(type: string): string {
    const typeConfig = this.schoolTypes.find(t => t.value === type);
    return typeConfig ? typeConfig.label : type;
  }

  /**
   * Gets school type color for styling
   */
  getSchoolTypeColor(type: string): string {
    const colors = {
      'PRIMARY': 'blue',
      'SECONDARY': 'green',
      'HIGHER': 'orange',
      'UNIVERSITY': 'purple'
    };
    return colors[type] || 'gray';
  }

  /**
   * Track by function for school list
   */
  trackBySchoolId(index: number, school: ISchool): string {
    return school.id || index.toString();
  }

  /**
   * Demo schools for UI purposes (fallback)
   */
  private getDemoSchools(): ISchool[] {
    return [
      {
        id: '1',
        name: 'King\'s College London',
        slug: 'kings-college-london',
        contact_email: 'info@kingscollege.edu',
        address: 'London, United Kingdom',
        logo: '',
        contact_phone: '+44 20 7848 2000',
        description: 'A prestigious university with world-class education',
        type: SchoolTypes.UNIVERSITY,
        settings: {} as any,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '2',
        name: 'Cambridge International School',
        slug: 'cambridge-international',
        contact_email: 'admissions@cambridge.edu',
        address: 'Cambridge, United Kingdom',
        logo: '',
        contact_phone: '+44 1223 334400',
        description: 'Excellence in secondary education',
        type: SchoolTypes.SECONDARY,
        settings: {} as any,
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '3',
        name: 'Oxford Primary Academy',
        slug: 'oxford-primary-academy',
        contact_email: 'info@oxfordprimary.edu',
        address: 'Oxford, United Kingdom',
        logo: '',
        contact_phone: '+44 1865 270000',
        description: 'Nurturing young minds for a bright future',
        type: SchoolTypes.PRIMARY,
        settings: {} as any,
        createdAt: new Date(),
        updatedAt: new Date()
      }
    ];
  }
}
